// Сохранение выбранной папки с реплеями и кэш разобранных реплеев.
// Папка хранится как FileSystemDirectoryHandle в IndexedDB (Chrome/Edge),
// чтобы после перезагрузки страницы не выбирать её заново.

import type { ReplayPrefill } from './replay-import';

export type ReplayPermission = 'granted' | 'prompt' | 'denied';

export interface ReplayFileHandle {
    kind: 'file';
    name: string;
    getFile(): Promise<File>;
}

export interface ReplayDirHandle {
    kind: 'directory';
    name: string;
    values(): AsyncIterableIterator<ReplayFileHandle | ReplayDirHandle>;
    queryPermission?(descriptor: { mode: 'read' }): Promise<ReplayPermission>;
    requestPermission?(descriptor: { mode: 'read' }): Promise<ReplayPermission>;
}

const DB_NAME = 'sc2ffa-games';
const DB_VERSION = 1;
const STORE_META = 'meta';
const STORE_CACHE = 'replayCache';
const DIR_KEY = 'replay-folder';

/** Сколько живёт закэшированный разбор реплея. */
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/** Максимум записей кэша, чтобы IndexedDB не разрасталась. */
const CACHE_MAX = 300;
/** Защита от слишком глубоких/зацикленных каталогов. */
const MAX_DEPTH = 12;

interface CacheRecord {
    key: string;
    prefill: ReplayPrefill;
    cachedAt: number;
}

function openDb(): Promise<IDBDatabase | null> {
    if (typeof indexedDB === 'undefined') return Promise.resolve(null);
    return new Promise((resolve) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE_META)) db.createObjectStore(STORE_META);
            if (!db.objectStoreNames.contains(STORE_CACHE)) {
                db.createObjectStore(STORE_CACHE, { keyPath: 'key' });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
    });
}

function requestToPromise<T>(req: IDBRequest<T>): Promise<T | null> {
    return new Promise((resolve) => {
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
    });
}

/** Поддерживает ли браузер выбор и сохранение папки. */
export function isDirectoryPickerSupported(): boolean {
    return typeof (window as unknown as { showDirectoryPicker?: unknown }).showDirectoryPicker === 'function';
}

/** Открывает системный выбор папки (только по жесту пользователя). */
export async function pickReplayDirectory(): Promise<ReplayDirHandle | null> {
    const picker = (window as unknown as {
        showDirectoryPicker?: (options?: { id?: string; mode?: 'read' }) => Promise<ReplayDirHandle>;
    }).showDirectoryPicker;
    if (!picker) return null;
    try {
        return await picker.call(window, { id: 'sc2ffa-replays', mode: 'read' });
    } catch (err) {
        // Отмена выбора — не ошибка.
        if ((err as DOMException)?.name === 'AbortError') return null;
        throw err;
    }
}

/** Запоминает папку между перезагрузками страницы. */
export async function rememberReplayDirectory(handle: ReplayDirHandle): Promise<void> {
    const db = await openDb();
    if (!db) return;
    try {
        await new Promise<void>((resolve) => {
            const tx = db.transaction(STORE_META, 'readwrite');
            tx.objectStore(STORE_META).put(handle, DIR_KEY);
            tx.oncomplete = () => resolve();
            tx.onerror = () => resolve();
            tx.onabort = () => resolve();
        });
    } catch {
        // Браузер может не разрешать хранить handle — папка не запомнится.
    }
    db.close();
}

/** Забывает выбранную папку. */
export async function forgetReplayDirectory(): Promise<void> {
    const db = await openDb();
    if (!db) return;
    await new Promise<void>((resolve) => {
        const tx = db.transaction(STORE_META, 'readwrite');
        tx.objectStore(STORE_META).delete(DIR_KEY);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
        tx.onabort = () => resolve();
    });
    db.close();
}

/** Возвращает сохранённую папку (без запроса прав). */
export async function getRememberedReplayDirectory(): Promise<ReplayDirHandle | null> {
    const db = await openDb();
    if (!db) return null;
    const handle = await requestToPromise(
        db.transaction(STORE_META, 'readonly').objectStore(STORE_META).get(DIR_KEY) as IDBRequest<ReplayDirHandle | undefined>
    );
    db.close();
    return handle && handle.kind === 'directory' ? handle : null;
}

/** Текущее разрешение на чтение папки. */
export async function queryReplayDirectoryPermission(handle: ReplayDirHandle): Promise<ReplayPermission> {
    if (!handle.queryPermission) return 'granted';
    try {
        return await handle.queryPermission({ mode: 'read' });
    } catch {
        return 'prompt';
    }
}

/** Запрашивает разрешение (работает только из клика пользователя). */
export async function requestReplayDirectoryPermission(handle: ReplayDirHandle): Promise<ReplayPermission> {
    if (!handle.requestPermission) return 'granted';
    try {
        return await handle.requestPermission({ mode: 'read' });
    } catch {
        return 'denied';
    }
}

function isReplayName(name: string): boolean {
    return /\.(sc2replay|replay)$/i.test(name);
}

async function collectReplayFiles(
    dir: ReplayDirHandle,
    out: File[],
    depth: number
): Promise<void> {
    if (depth > MAX_DEPTH) return;
    for await (const entry of dir.values()) {
        if (entry.kind === 'file') {
            if (!isReplayName(entry.name)) continue;
            try {
                out.push(await entry.getFile());
            } catch {
                // Файл могли удалить/переместить — пропускаем.
            }
        } else {
            await collectReplayFiles(entry, out, depth + 1);
        }
    }
}

/** Все реплеи в папке (рекурсивно), свежие сверху. */
export async function listReplayFiles(handle: ReplayDirHandle): Promise<File[]> {
    const files: File[] = [];
    try {
        await collectReplayFiles(handle, files, 0);
    } catch {
        return [];
    }
    return files.sort((a, b) => b.lastModified - a.lastModified);
}

/** Версия формата разбора: меняем, когда меняется логика сервера (кэш инвалидируется). */
const CACHE_VERSION = 5;

/** Ключ кэша: файл + размер + время изменения + версия разбора. */
export function replayCacheKey(file: File): string {
    return `v${CACHE_VERSION}:${file.name}:${file.size}:${file.lastModified}`;
}

/** Чистит устаревшие записи и ограничивает размер кэша. */
export async function pruneReplayCache(): Promise<void> {
    const db = await openDb();
    if (!db) return;
    const all = await requestToPromise(
        db.transaction(STORE_CACHE, 'readonly').objectStore(STORE_CACHE).getAll() as IDBRequest<CacheRecord[]>
    );
    if (!all) {
        db.close();
        return;
    }
    const now = Date.now();
    const alive = all.filter((record) => now - record.cachedAt < CACHE_TTL_MS);
    const expired = all.filter((record) => now - record.cachedAt >= CACHE_TTL_MS);
    alive.sort((a, b) => b.cachedAt - a.cachedAt);
    const overLimit = alive.slice(CACHE_MAX);
    const toDelete = [...expired, ...overLimit];
    if (toDelete.length > 0) {
        await new Promise<void>((resolve) => {
            const tx = db.transaction(STORE_CACHE, 'readwrite');
            const store = tx.objectStore(STORE_CACHE);
            for (const record of toDelete) store.delete(record.key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => resolve();
            tx.onabort = () => resolve();
        });
    }
    db.close();
}

/** Берёт разбор реплея из кэша (если он свежий). */
export async function getCachedReplay(file: File): Promise<ReplayPrefill | null> {
    const db = await openDb();
    if (!db) return null;
    const key = replayCacheKey(file);
    const record = await requestToPromise(
        db.transaction(STORE_CACHE, 'readonly').objectStore(STORE_CACHE).get(key) as IDBRequest<CacheRecord | undefined>
    );
    db.close();
    if (!record) return null;
    if (Date.now() - record.cachedAt >= CACHE_TTL_MS) {
        void pruneReplayCache();
        return null;
    }
    return record.prefill;
}

/** Кладёт разбор реплея в кэш. */
export async function cacheReplay(file: File, prefill: ReplayPrefill): Promise<void> {
    const db = await openDb();
    if (!db) return;
    const record: CacheRecord = {
        key: replayCacheKey(file),
        prefill,
        cachedAt: Date.now(),
    };
    await new Promise<void>((resolve) => {
        const tx = db.transaction(STORE_CACHE, 'readwrite');
        tx.objectStore(STORE_CACHE).put(record);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
        tx.onabort = () => resolve();
    });
    db.close();
}
